import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Download, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { useModels } from "@/hooks/useModels";
import { MaskLines } from "@/components/site/Motion";

const KEYS = ["height", "bust", "waist", "hips", "shoes", "hair", "eyes"];

const Lightbox = ({ images, index, setIndex }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, setIndex]);
  return (
    <div data-testid="lightbox" className="fixed inset-0 z-[60] flex items-center justify-center bg-[#120d10]/95 p-4" onClick={() => setIndex(null)}>
      <img src={images[index]} alt="" className="max-h-[88vh] max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
      <button data-testid="lightbox-close" className="absolute right-5 top-5 text-[#f4ede6]" onClick={() => setIndex(null)}><X className="h-8 w-8" /></button>
      <button data-testid="lightbox-prev" className="absolute left-3 grid h-12 w-12 place-items-center rounded-full bg-[#1c1418] text-white" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + images.length) % images.length); }}><ArrowLeft /></button>
      <button data-testid="lightbox-next" className="absolute right-3 grid h-12 w-12 place-items-center rounded-full bg-[#1c1418] text-white" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % images.length); }}><ArrowRight /></button>
      <span className="absolute bottom-5 font-mono text-xs text-[#f4ede6]">{index + 1} / {images.length}</span>
    </div>
  );
};

const Stats = ({ m }) => (
  <table className="mt-8 w-full" data-testid="model-measurements">
    <tbody>
      {KEYS.map((k) => {
        const [metric, imperial] = (m.measurements?.[k] || "—").split(" - ");
        return (
          <tr key={k} className="border-b border-[#1c1418]/20">
            <td className="py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/60">{k}</td>
            <td className="py-3 text-right font-display text-2xl uppercase">{metric}</td>
            <td className="w-24 py-3 text-right font-mono text-xs text-[#1c1418]/60">{imperial || ""}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

export default function ModelDetail() {
  const { slug } = useParams();
  const { models } = useModels();
  const [m, setM] = useState(null);
  const [error, setError] = useState(false);
  const [lb, setLb] = useState(null);

  useEffect(() => {
    setM(null); setError(false);
    api.get(`/models/${slug}`).then((r) => setM(r.data)).catch(() => setError(true));
  }, [slug]);

  const idx = models.findIndex((x) => x.slug === slug);
  const prev = idx >= 0 ? models[(idx - 1 + models.length) % models.length] : null;
  const next = idx >= 0 ? models[(idx + 1) % models.length] : null;

  if (error) return <main className="min-h-screen bg-white px-5 pt-40 text-center font-display text-5xl uppercase" data-testid="model-not-found">Model not found. <Link className="text-[#1c1418]/60" to="/models">Back</Link></main>;
  if (!m) return <main className="min-h-screen bg-white" />;
  const images = Array.isArray(m.images) ? m.images : [];

  return (
    <main data-testid="model-detail-page" className="bg-white px-5 pb-24 pt-28 text-[#1c1418] md:px-10">
      <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-12">
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <Link to="/models" data-testid="back-to-board" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] hover:text-[#1c1418]/60"><ArrowLeft className="h-4 w-4" /> The board</Link>
            <MaskLines key={slug} as="h1" lines={m.name.split(" ")} className="mt-6 font-display text-7xl uppercase leading-[0.85] md:text-8xl" />
            <div className="mt-4 w-fit -rotate-2 bg-[#1c1418] text-white px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em]">{images.length} pictures · Book</div>
            <Stats m={m} />
            {m.instagram && <a href={`https://instagram.com/${m.instagram}`} target="_blank" rel="noreferrer" data-testid="model-instagram" className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] hover:text-[#1c1418]/60">@{m.instagram} <ArrowUpRight className="h-4 w-4" /></a>}
            <a href={`mailto:scout@youthmanagement.com?subject=Booking request — ${m.name}`} data-testid="model-book-button"
              className="mt-8 flex w-full items-center justify-between bg-[#1c1418] px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#f4ede6] transition-colors hover:bg-[#333333]">
              Request booking <ArrowUpRight className="h-4 w-4 text-[#e9458f]" />
            </a>
            <a href={`${API}/models/${m.slug}/composite.pdf`} data-testid="model-composite-download"
              className="mt-3 flex w-full items-center justify-between border border-[#1c1418] px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] transition-colors hover:bg-[#f4f4f4]">
              Download composite PDF <Download className="h-4 w-4 text-[#e9458f]" />
            </a>
            {prev && next && (
              <div className="mt-6 flex justify-between font-mono text-[11px] uppercase tracking-[0.2em]">
                <Link to={`/models/${prev.slug}`} data-testid="model-prev" className="flex items-center gap-2 hover:text-[#1c1418]/60"><ArrowLeft className="h-4 w-4" />{prev.name}</Link>
                <Link to={`/models/${next.slug}`} data-testid="model-next" className="flex items-center gap-2 hover:text-[#1c1418]/60">{next.name}<ArrowRight className="h-4 w-4" /></Link>
              </div>
            )}
          </div>
        </aside>
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-8" data-testid="model-gallery">
          {images.map((src, i) => (
            <motion.button key={src} onClick={() => setLb(i)} data-testid={`gallery-image-${i}`}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className={`group overflow-hidden bg-[#1c1418] ${i % 5 === 0 ? "sm:col-span-2 aspect-[4/5] sm:aspect-[16/11]" : "aspect-[2/3]"}`}>
              <img src={src} alt={`${m.name} ${i + 1}`} loading="lazy" className="h-full w-full object-cover object-[center_30%] transition-transform duration-[1.2s] group-hover:scale-[1.04]" />
            </motion.button>
          ))}
        </section>
      </div>
      {lb !== null && images.length > 0 && <Lightbox images={images} index={lb} setIndex={setLb} />}
    </main>
  );
}
